import type { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import type {
	Beautify,
	EmptyObject,
	Maybe,
	MergeTypes,
	WithOptional,
	OptionalKeys,
	WithRequired,
	RequiredKeys,
	NonEmptyArray,
} from '@/helpers/types';
import type { SnowplowContext } from '@/helpers/tracking/providers';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

/** Normal tracking property options + an optional trigger check method. */
export interface TrackingSpec extends TrackingProviderPropertiesInterface {
	/** Required tracking contexts for the event. */
	contexts?: NonEmptyArray<SnowplowContext>;

	/**
	 * A callback function to further validate the event.
	 * If it returns false, the event will not be sent.
	 */
	triggerCheck?: (args: any) => boolean;

	/**
	 * **DO NOT SET**
	 * It is an internal property for Spectre.
	 * This property is present if the spec used `defineSpec`
	 */
	_defined?: true;
}

/**
 * Full record of the tracking specs for a feature, experiment, or whatever else.
 *
 * For type safety while writing the specs, include `satisfies TrackingSpecs` at the end.
 *
 * @example
 * ```ts
 * const specs = {
 * 	eventOne: {
 * 		action: 'event_one'
 * 		label: 'event'
 * 	},
 * 	eventTwo: {
 * 		action: 'event_two'
 * 		label: 'event'
 * 		triggerCheck: (providerId: number) => [350, 9, 119].includes(providerId)
 * 	},
 * } satisfies TrackingSpecs;
 * ```
 */
export type TrackingSpecs = Record<string, TrackingSpec>;

/**
 * A more typesafe and extensible alternative to define a tracking spec.
 *
 * **WATCH OUT FOR THE CURRY!**
 * Due to a limitation with TS, the empty call is needed to curry the first type passed
 * and then properly infer the type of the passed object.
 *
 * The rabbit hole on this one is deep, you can find more info here:
 * https://zustand.docs.pmnd.rs/guides/typescript#:~:text=TLDR%3A%20It%20is%20a%20workaround%20for%20microsoft/TypeScript%2310571.
 *
 * @example
 * ```ts
 * const specs = {
 * 	eventTwo: defineSpec<{
 * 		label: 'open' | 'close',
 * 		contexts: [SnowplowTitleContext, SnowplowClickoutContext]
 * 	}>()({
 * 		action: 'event_two'
 * 		triggerCheck: (providerId: number) => [350, 9, 119].includes(providerId)
 * 	}),
 * }
 * ```
 */
export function defineSpec<Generic extends TrackingSpec>() {
	// Disallow redefining properties from the Generic type in the runtime Spec
	return (spec?: Omit<TrackingSpec, keyof Generic>) => {
		type Spec = typeof spec;

		// Beautify: make the hover type more legible
		// WithOptional: keys passed to the function are already in the spec so they can be optional
		// MergeTypes: Combine the
		// 		optional: (curried) spec argument type and the rest of the TrackingSpec options
		// 		required: the generic passed first as those are meant to be filled out
		//
		// `_defined?: true` is added so that these specs can be differentiated.
		// It is marked as optional because it is type-only and shouldn't be in the spec
		return spec as Beautify<
			WithOptional<MergeTypes<Spec, Generic, TrackingSpec>, keyof Spec> & { _defined?: true }
		>;
	};
}

/** Returns a type-safe function to handle all the tracking for a feature as per the given specs. */
export function createSpecTracker<Specs extends TrackingSpecs>({
	specs,
	baseProperties = null,
	baseContexts = () => [],
}: {
	/** Tracking specs for the feature. */
	specs: Specs;

	/** Tracking properties to include with every event, can be overwritten. */
	baseProperties?: TrackingProviderPropertiesInterface | null;

	/** Contexts to include with every event. (Getter for reactivity). */
	baseContexts?: () => Maybe<SnowplowContext>[];
}) {
	/**
	 * The event data passed to trigger check callbacks.
	 * Essentially the whole event minus the trigger check itself.
	 */
	type EventCallback<Event extends keyof Specs> = (data: Omit<Specs[Event], 'triggerCheck'>) => void;

	/** Type for the map of trigger check callbacks attached to events. */
	type EventCallbackMap<Specs extends TrackingSpecs> = {
		[Event in keyof Specs]?: Array<(data: Omit<Specs[Event], 'triggerCheck'>) => void>;
	};

	/** Helper to get the type of the trigger check function's params. */
	type TriggerCheckParams<Event extends keyof Specs> = Specs[Event] extends {
		triggerCheck: (args: infer Params) => boolean;
	}
		? Params
		: null;

	/** Map of the trigger check callbacks attached to each event. */
	const listeners: EventCallbackMap<Specs> = {};

	// prettier-ignore
	/**
	 * *Abstract* avoid using it directly.
	 * All the options that are required to track the event.
	 *
	 * They're all listed here as optional, further types decide what is
	 * required depending on the actual event being tracked.
	 */
	interface AbstractTrackOptions<Event extends keyof Specs> {
		/**
		 * Supports overwritting any of the fields in the event's spec.
		 * Properties added with `defineSpec` are required to be filled in.
		 *
		 * `contexts` and `triggerCheck` cannot be redefined.
		 */
		spec?: Omit<Specs[Event], 'contexts' | 'triggerCheck'>;

		/** The arguments defined for the triggerCheck callback as defined in the spec. */
		checkArgs?: TriggerCheckParams<Event>;

		/**
		 * The first items in the context array need to match those defined in the spec.
		 * Since it is defined as a tuple, the order of the contexts matters.
		 *
		 * The last argument is a nested array of any other arbitrary contexts you want to add.
		 *
		 * If there are no defined contexts in the spec, you can directly pass arbitrary contexts.
		 * Wrapping those in a nested array is optional.
		 */
		contexts?:
			// array of the defined contexts
			| Specs[Event]['contexts']
			// list of the defined contexts followed by a *nested array* of arbitrary other contexts
			| [...NonNullable<Specs[Event]['contexts']>, NonEmptyArray<SnowplowContext>]
			// or arbitrary contexts
			| SnowplowContext[]

		/** If true, sends the event immediately without queuing (use for navigation/unload events) */
		immediate?: boolean;
	}

	// prettier-ignore
	/**
	 * Builds the union of option keys that are required. Depends on the event.
	 *
	 * As new options get added to `AbstractTrackOptions`, add a condition here for when
	 * it should be required when calling `track`.
	 */
	type RequiredTrackArguments<Event extends keyof Specs> =
		// If a spec uses `defineSpec`, the type-only properties are required
		| (NonNullable<Specs[Event]['_defined']> extends true ? 'spec' : never)
		// If a spec has a trigger check, the params for the check are required
		| ('triggerCheck' extends RequiredKeys<Specs[Event]> ? 'checkArgs' : never)
		// If a spec has contexts they are required for tracking
		| ('contexts' extends RequiredKeys<Specs[Event]> ? 'contexts' : never)

	/**
	 * The *concrete* type of `AbstractTrackOptions` which, given an event, marks
	 * the proper options as required or not.
	 */
	type TrackOptions<Event extends keyof Specs> = Beautify<
		WithRequired<AbstractTrackOptions<Event>, RequiredTrackArguments<Event>>
	>;

	// The final type of the arguments to `track`.
	// If all the properties of `TrackOptions` end up optional, then you don't need to pass
	// the second argument to `track`.
	// This type offers better DX when calling `track`

	/** Arguments required for calling `track` for an event. */
	type TrackArguments<Event extends keyof Specs> = Omit<
		TrackOptions<Event>,
		OptionalKeys<TrackOptions<Event>>
	> extends EmptyObject
		? [Event] | [Event, TrackOptions<Event>]
		: [Event, TrackOptions<Event>];

	/**
	 * Send a tracking event using the event's defined spec.
	 *
	 * ```ts
	 * // Basic Usage
	 * // 1. Simple event
	 * track('eventName');
	 *
	 * // 2. Event made with `defineSpec`
	 * const eventName = defineSpec<{ label: 'hello' | 'world' }>()();
	 * track('eventName', { spec: { label: 'hello' } });
	 *
	 * // Passing Contexts
	 *
	 * // 0. Spec definition
	 * const eventName = defineSpec<{ contexts: [SnowplowTitleContext] }>()();
	 *
	 * // 1. Required context only
	 * track('eventName', { contexts: [titleContext] });
	 *
	 * // 2. With arbirary contexts. Make sure to pass those in their own array for TS.
	 * track('eventName', { contexts: [titleContext, [arbitrary contexts]] });
	 * ```
	 */
	function track<Event extends keyof Specs>(...args: TrackArguments<Event>) {
		const event = args[0];
		if (event == null) return;

		const { spec, checkArgs, contexts, immediate = false } = args[1] ?? {};

		const { triggerCheck, ...specProperties } = { ...specs[event], ...spec };
		const trackingProperties = { ...baseProperties, ...specProperties };

		listeners[event]?.forEach(cb => cb(trackingProperties));

		const fullContexts = baseContexts()
			.concat((contexts ?? []).flat()) // `.flat()` to handle nested arrays of arbitrary contexts
			.filter(context => context != null) as SnowplowContext[];

		if (triggerCheck != null && !triggerCheck(checkArgs)) return;

		// Specs define category next to other properties but `trackEvent` uses the first argument instead
		const { category = 'userinteraction', ...eventProperties } = trackingProperties;
		return TrackingHelper.trackEvent(category, eventProperties, fullContexts, false, immediate);
	}

	/** Run a callback before the event is fired. Lets you modify the tracking specs. */
	track.on = function <Event extends keyof Specs>(event: Event, cb: EventCallback<Event>) {
		if (listeners[event] == null) {
			listeners[event] = [];
		}

		// Avoid adding the same callback multiple times
		if (listeners[event]!.some(callback => callback === cb)) return;

		listeners[event]!.push(cb);
	};

	/** Remove an event listener callback. */
	track.off = function <Event extends keyof Specs>(event: Event, cb: EventCallback<Event>) {
		if (listeners[event] == null) return;
		listeners[event] = listeners[event]!.filter(callback => callback !== cb);
	};

	/** Readonly tracking specs defined for this Spec Tracker. */
	track.specs = specs as Readonly<Specs>;
	Object.defineProperty(track, 'specs', { writable: false });

	return track;
}
